import React from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  Stack,
  Popover,
  Tooltip,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import { parsePrompt } from "../UIUtils.ts";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

interface HistorySidebarProps {
  isOpen: boolean;
  agentRuns: any[];
  selectedRunNumber: number | null;
  hasMoreHistory: boolean;
  isLoadingMoreHistory: boolean;
  totalHistoryRuns: number;
  onToggleOpen: () => void;
  onToggleClose: () => void;
  onSelectRun: (run: any) => void;
  onLoadMore: () => void;
}

const HistorySidebar: React.FC<HistorySidebarProps> = ({
  isOpen,
  agentRuns,
  selectedRunNumber,
  hasMoreHistory,
  isLoadingMoreHistory,
  totalHistoryRuns,
  onToggleOpen,
  onToggleClose,
  onSelectRun,
  onLoadMore,
}) => {
  const [popoverAnchor, setPopoverAnchor] = React.useState<HTMLElement | null>(
    null,
  );
  const [popoverPrompt, setPopoverPrompt] = React.useState("");

  const getPromptSnippet = (prompt: string) => {
    return prompt.length > 50 ? prompt.substring(0, 40) + "..." : prompt;
  };

  const handleOpenPromptPopover = (
    event: React.MouseEvent<HTMLElement>,
    prompt: string,
  ) => {
    event.stopPropagation();
    setPopoverAnchor(event.currentTarget);
    setPopoverPrompt(prompt);
  };

  const handleClosePromptPopover = () => {
    setPopoverAnchor(null);
    setPopoverPrompt("");
  };

  return (
    <>
      {/* EXPAND BUTTON: Only visible when history is closed */}
      {!isOpen && (
        <IconButton
          onClick={onToggleOpen}
          sx={{
            position: "absolute",
            left: 0,
            top: 16,
            zIndex: 10,
            bgcolor: "white",
            border: "1px solid rgba(0,0,0,0.45)",
            borderRadius: "0 8px 8px 0",
            "&:hover": { bgcolor: "#f0f0f0" },
            mt: 2,
          }}
          size="small"
        >
          <HistoryIcon fontSize="small" />
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      )}

      {/* LEFT SIDEBAR: Run History */}
      <Box
        sx={{
          width: isOpen ? { xs: "180px", sm: "200px", md: "220px" } : "0px",
          minWidth: isOpen ? { xs: "180px", sm: "200px", md: "220px" } : "0px",
          maxWidth: isOpen ? { xs: "180px", sm: "200px", md: "220px" } : "0px",
          transition: "width 0.3s ease",
          overflow: "hidden",
          borderRight: isOpen ? "1px solid rgba(0,0,0,0.12)" : "none",
          display: "flex",
          flexDirection: "column",
          whiteSpace: "nowrap",
          mt: 3.3,
        }}
      >
        <Box
          sx={{
            pl: 2,
            pr: 1,
            pb: 1,
            pt: 0.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <HistoryIcon />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            History
          </Typography>
          {/* COLLAPSE BUTTON: Visible when sidebar is open */}
          <IconButton size="small" onClick={onToggleClose}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Box>

        <List
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            px: 0.2,
            "&::-webkit-scrollbar": { width: "9px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.1)",
              borderRadius: "10px",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.2)" },
            },
          }}
        >
          {agentRuns.map((run) => (
            <ListItem key={run.run_number} disablePadding sx={{ mb: 0.1 }}>
              <ListItemButton
                selected={selectedRunNumber === run.run_number}
                sx={{ borderRadius: 2 }}
                onClick={() => onSelectRun(run)}
              >
                <Stack direction="column" spacing={0.5} sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <ListItemText
                      primary={`Run #${run.run_number}`}
                      secondary={`V ${run.agent_version}`}
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: 600,
                      }}
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                    {run?.artifact_count > 0 && (
                      <Tooltip title="Artifacts">
                        <IconButton
                          size="small"
                          onClick={() => {}}
                          sx={{ mb: 2.5, color: "primary.main" }}
                        >
                          <AutoAwesomeIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
                  >
                    {(() => {
                      const fullPrompt = parsePrompt(run?.agent_prompt);
                      const snippet = getPromptSnippet(fullPrompt);
                      const isTruncated = fullPrompt.length > 50;
                      const baseText = isTruncated
                        ? snippet.slice(0, -3)
                        : snippet;

                      return (
                        <>
                          {baseText}
                          {isTruncated && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={(event) =>
                                handleOpenPromptPopover(event, fullPrompt)
                              }
                              sx={{
                                minWidth: 0,
                                //   height: 22,
                                px: 0.5,
                                ml: 0.5,
                                textTransform: "none",
                                lineHeight: 0.5,
                                borderRadius: 3,
                              }}
                            >
                              ...
                            </Button>
                          )}
                        </>
                      );
                    })()}
                  </Typography>
                  <Divider />
                </Stack>
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Popover
          open={Boolean(popoverAnchor)}
          anchorEl={popoverAnchor}
          onClose={handleClosePromptPopover}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              p: 2,
              maxWidth: 360,
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            },
          }}
        >
          <Typography variant="body2">{popoverPrompt}</Typography>
        </Popover>

        {/* Load More Button or Summary */}
        <Box sx={{ p: 2, borderTop: "1px solid rgba(0,0,0,0.08)" }}>
          {hasMoreHistory ? (
            <Button
              fullWidth
              variant="outlined"
              size="small"
              onClick={onLoadMore}
              disabled={isLoadingMoreHistory}
              sx={{
                textTransform: "none",
                borderRadius: 99,
              }}
            >
              {isLoadingMoreHistory ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Loading...
                </>
              ) : (
                `Load More (${agentRuns.length} of ${totalHistoryRuns})`
              )}
            </Button>
          ) : (
            <Typography
              variant="caption"
              sx={{
                textAlign: "center",
                display: "block",
                color: "text.secondary",
              }}
            >
              {agentRuns.length > 0
                ? `All ${totalHistoryRuns} runs loaded`
                : "No runs yet"}
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

export default HistorySidebar;
